import React, { useState, useEffect, useContext, useRef } from 'react';
import { API, AuthContext } from '../../App';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, Search, RefreshCw, Shield, Briefcase, Building2,
  Trash2, Eye, EyeOff, LogIn, AlertTriangle,
  MoreVertical, Download, User, Phone, Mail,
  Calendar, ChevronDown, Check, X, CreditCard
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';

/* ── helpers ──────────────────────────────────────────────────────────── */
const AVATAR_COLORS = [
  'bg-purple-500', 'bg-teal-500', 'bg-blue-500', 'bg-amber-400',
  'bg-pink-500', 'bg-emerald-500', 'bg-sky-500', 'bg-orange-400',
];
function avatarColor(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h += name.charCodeAt(i);
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name = '') {
  const parts = name.trim().split(' ');
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}
function subLabel(status) {
  const map = { trial: 'Deneme', active: 'Aktif', inactive: 'Askıda', canceled: 'İptal', expired: 'Süresi Doldu' };
  return map[status] || status || '—';
}
function subColor(status) {
  const map = { trial: 'bg-blue-100 text-blue-700', active: 'bg-green-100 text-green-700', inactive: 'bg-gray-100 text-gray-500', canceled: 'bg-red-100 text-red-600', expired: 'bg-red-100 text-red-600' };
  return map[status] || 'bg-gray-100 text-gray-500';
}
function planBadgeColor(status) {
  const map = { trial: 'bg-sky-100 text-sky-700', active: 'bg-green-100 text-green-800', inactive: 'bg-gray-100 text-gray-500', canceled: 'bg-red-100 text-red-600', expired: 'bg-red-100 text-red-600' };
  return map[status] || 'bg-gray-100 text-gray-500';
}
function roleLabel(role) {
  const map = { ultra_admin: 'Ultra Admin', organizer: 'Organizatör', team_member: 'Ekip Üyesi', company: 'Firma' };
  return map[role] || role;
}
function empId(id = '') { return `EMP-${id.replace(/-/g, '').slice(0, 10).toUpperCase()}`; }
function daysRemaining(trialEndsAt) {
  if (!trialEndsAt) return null;
  const diff = new Date(trialEndsAt) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

/* ── DropdownMenu ─────────────────────────────────────────────────────── */
function DropMenu({ children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors">
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-50 w-44 bg-white border border-gray-200 rounded-xl shadow-lg py-1 overflow-hidden">
          {React.Children.map(children, child => child && React.cloneElement(child, { onClick: (e) => { child.props.onClick?.(e); setOpen(false); } }))}
        </div>
      )}
    </div>
  );
}
function DropItem({ icon: Icon, label, onClick, danger }) {
  return (
    <button onClick={onClick} className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 transition-colors text-left ${danger ? 'text-red-600' : 'text-gray-700'}`}>
      {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}
      {label}
    </button>
  );
}

/* ── FilterDropdown ───────────────────────────────────────────────────── */
function FilterDropdown({ value, onChange, options, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const current = options.find(o => o.value === value) || options[0];
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)} className="h-8 px-3 border border-gray-200 rounded-lg text-sm text-gray-700 flex items-center gap-2 hover:bg-gray-50 transition-colors min-w-[120px]">
        {current.dot && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: current.dot }} />}
        <span className="flex-1 text-left truncate">{current.label}</span>
        <ChevronDown className="w-3 h-3 shrink-0 text-gray-400" />
      </button>
      {open && (
        <div className="absolute left-0 top-9 z-50 w-48 bg-white border border-gray-200 rounded-xl shadow-lg py-1 overflow-hidden">
          {options.map(o => (
            <button key={o.value} onClick={() => { onChange(o.value); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 transition-colors text-gray-700">
              {o.dot ? <span className="w-2 h-2 rounded-full shrink-0" style={{ background: o.dot }} /> : <span className="w-2 h-2" />}
              {o.label}
              {value === o.value && <Check className="w-3.5 h-3.5 text-teal-500 ml-auto shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const STATUS_OPTS = [
  { value: 'all', label: 'Tüm Durumlar', dot: '#6b7280' },
  { value: 'active', label: 'Aktif', dot: '#22c55e' },
  { value: 'trial', label: 'Deneme', dot: '#3b82f6' },
  { value: 'inactive', label: 'Askıda', dot: '#f59e0b' },
  { value: 'expired', label: 'Süresi Dolmuş', dot: '#ef4444' },
];
const PLAN_OPTS = [
  { value: 'all', label: 'Tüm Planlar', dot: '#6b7280' },
  { value: 'trial', label: 'Free Trial', dot: '#3b82f6' },
  { value: 'active', label: 'Ücretli', dot: '#22c55e' },
  { value: 'inactive', label: 'Askıda', dot: '#f59e0b' },
  { value: 'expired', label: 'Süresi Dolmuş', dot: '#ef4444' },
];
const PER_PAGE_OPTS = [
  { value: '25', label: '25 / sayfa' }, { value: '50', label: '50 / sayfa' }, { value: '100', label: '100 / sayfa' },
];

/* ════════════════════════════════════════════════════════════════════════
   Main Component
═══════════════════════════════════════════════════════════════════════ */
export default function AdminUsers() {
  const { setUser } = useContext(AuthContext);

  // Data
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Left panel
  const [selectedOrg, setSelectedOrg] = useState('all'); // 'all' | organizer id
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [orgSearch, setOrgSearch] = useState('');

  // Right panel
  const [userSearch, setUserSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  const [rightStatus, setRightStatus] = useState('all');
  const [perPage, setPerPage] = useState('25');

  // Modals
  const [detailUser, setDetailUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [superPassword, setSuperPassword] = useState('');
  const [showSuperPwd, setShowSuperPwd] = useState(false);
  const [superPwdError, setSuperPwdError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data);
    } catch { toast.error('Kullanıcılar yüklenemedi'); }
    finally { setLoading(false); }
  };

  /* ── Existing action handlers (unchanged) ─────────────────────────── */
  const handleImpersonate = async (user) => {
    setActionLoading(user.id + '_imp');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/users/${user.id}/impersonate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.setItem('admin_token', token);
      localStorage.setItem('token', res.data.token);
      setUser(res.data.user);
      toast.success(`${user.name} hesabına erişildi. Geri dönmek için çıkış yapın.`);
      window.location.href = '/organizer';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erişim sağlanamadı');
    } finally { setActionLoading(null); }
  };

  const handleToggleStatus = async (user) => {
    setActionLoading(user.id + '_status');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(`${API}/admin/users/${user.id}/toggle-status`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const newStatus = res.data.is_active;
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_active: newStatus } : u));
      toast.success(newStatus ? `${user.name} aktif edildi` : `${user.name} pasife alındı`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    } finally { setActionLoading(null); }
  };

  const handleDelete = async (user) => {
    if (!superPassword.trim()) { setSuperPwdError('Süper şifre gerekli'); return; }
    setActionLoading(user.id);
    setSuperPwdError('');
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { super_password: superPassword }
      });
      toast.success(`${user.name} silindi`);
      setUsers(prev => prev.filter(u => u.id !== user.id));
      setDeleteTarget(null);
      setSuperPassword('');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Silinemedi';
      if (msg.includes('şifre') || msg.includes('password')) setSuperPwdError(msg);
      else toast.error(msg);
    } finally { setActionLoading(null); }
  };

  /* ── Derived data ─────────────────────────────────────────────────── */
  const nonAdmins = users.filter(u => u.role !== 'ultra_admin');
  const organizers = users.filter(u => u.role === 'organizer');

  // Left panel: filter organizers
  const filteredOrgs = organizers.filter(o => {
    const matchSearch = o.name?.toLowerCase().includes(orgSearch.toLowerCase()) || o.email?.toLowerCase().includes(orgSearch.toLowerCase());
    const matchStatus = statusFilter === 'all' || o.subscription_status === statusFilter;
    const matchPlan = planFilter === 'all' || o.subscription_status === planFilter;
    return matchSearch && matchStatus && matchPlan;
  });

  // Right panel: users for selected org
  const rightUsers = (() => {
    let base = selectedOrg === 'all'
      ? nonAdmins
      : users.filter(u => u.id === selectedOrg || u.organizer_id === selectedOrg);

    if (userSearch) base = base.filter(u => u.name?.toLowerCase().includes(userSearch.toLowerCase()));
    if (emailSearch) base = base.filter(u => u.email?.toLowerCase().includes(emailSearch.toLowerCase()));
    if (rightStatus !== 'all') {
      if (rightStatus === 'active') base = base.filter(u => u.is_active !== false);
      else base = base.filter(u => u.is_active === false);
    }
    return base.slice(0, parseInt(perPage));
  })();

  const selectedOrgData = selectedOrg === 'all' ? null : organizers.find(o => o.id === selectedOrg);
  const rightActive = rightUsers.filter(u => u.is_active !== false).length;
  const rightPassive = rightUsers.filter(u => u.is_active === false).length;

  // Stats
  const totalActive = nonAdmins.filter(u => u.is_active !== false).length;
  const totalPassive = nonAdmins.filter(u => u.is_active === false).length;

  /* ── User Detail helper: find org for a team member ──────────────── */
  const getOrgForUser = (user) => {
    if (user.role === 'organizer') return user;
    return organizers.find(o => o.id === user.organizer_id) || null;
  };

  return (
    <div className="p-6 space-y-5" data-testid="admin-users">

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kullanıcı Yönetimi</h1>
          <p className="text-sm text-gray-500 mt-0.5">Tüm platform kullanıcılarını şirketlere göre yönetin</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchUsers} disabled={loading} data-testid="refresh-users" className="gap-1.5 h-8">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Yenile
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5 h-8">
            <Download className="w-3.5 h-3.5" /> Export
          </Button>
        </div>
      </div>

      {/* ── Stats ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Toplam Şirket', value: organizers.length, icon: Building2, bg: 'bg-purple-50', color: 'text-purple-500', border: 'border-purple-100' },
          { label: 'Toplam Kullanıcı', value: nonAdmins.length, icon: Users, bg: 'bg-blue-50', color: 'text-blue-500', border: 'border-blue-100' },
          { label: 'Aktif Kullanıcı', value: totalActive, icon: User, bg: 'bg-green-50', color: 'text-green-500', border: 'border-green-100' },
          { label: 'Pasif Kullanıcı', value: totalPassive, icon: Shield, bg: 'bg-red-50', color: 'text-red-400', border: 'border-red-100' },
        ].map(s => (
          <div key={s.label} className={`bg-white border ${s.border} rounded-xl p-4 flex items-center justify-between`} data-testid={`stat-${s.label}`}>
            <div>
              <p className="text-xs text-gray-500 font-medium">{s.label}</p>
              <p className="text-2xl font-bold text-gray-900 mt-0.5">{s.value}</p>
            </div>
            <div className={`${s.bg} p-2.5 rounded-lg`}>
              <s.icon className={`w-5 h-5 ${s.color}`} />
            </div>
          </div>
        ))}
      </div>

      {/* ── Two-panel layout ────────────────────────────────────────── */}
      <div className="flex gap-4 min-h-[600px]">

        {/* ── LEFT PANEL: Company List ──────────────────────────────── */}
        <div className="w-72 shrink-0 bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
            <span className="text-sm font-semibold text-gray-800">Şirketler ({filteredOrgs.length})</span>
          </div>

          <div className="px-3 pt-3 space-y-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                value={orgSearch}
                onChange={e => setOrgSearch(e.target.value)}
                placeholder="Şirket ara..."
                className="w-full h-8 pl-8 pr-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-400"
              />
            </div>
            {/* Status filter */}
            <FilterDropdown value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTS} />
            {/* Plan filter */}
            <FilterDropdown value={planFilter} onChange={setPlanFilter} options={PLAN_OPTS} />
            <p className="text-[10px] text-gray-400 pb-1">{filteredOrgs.length} şirket görünüyor</p>
          </div>

          {/* Company list */}
          <div className="flex-1 overflow-y-auto">
            {/* All users item */}
            <button
              onClick={() => setSelectedOrg('all')}
              className={`w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors border-b border-gray-100 ${selectedOrg === 'all' ? 'bg-teal-50 border-l-2 border-l-teal-500' : ''}`}
              data-testid="org-all"
            >
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                <Users className="w-4 h-4 text-gray-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-900 truncate">Tüm Kullanıcılar</p>
                <p className="text-[10px] text-gray-400">{nonAdmins.length} toplam kullanıcı</p>
              </div>
            </button>

            {filteredOrgs.map(org => {
              const memberCount = users.filter(u => u.organizer_id === org.id).length + 1;
              const isSelected = selectedOrg === org.id;
              return (
                <button
                  key={org.id}
                  onClick={() => setSelectedOrg(org.id)}
                  className={`w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors border-b border-gray-100 ${isSelected ? 'bg-teal-50 border-l-2 border-l-teal-500' : ''}`}
                  data-testid={`org-item-${org.id}`}
                >
                  <div className={`w-8 h-8 rounded-lg ${avatarColor(org.name)} flex items-center justify-center shrink-0`}>
                    <span className="text-xs font-bold text-white">{(org.name || '?')[0].toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-900 truncate">{org.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[10px] text-gray-400">{memberCount} kullanıcı</span>
                      {org.subscription_status && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${subColor(org.subscription_status)}`}>
                          {subLabel(org.subscription_status)}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}

            {filteredOrgs.length === 0 && (
              <div className="py-8 text-center text-xs text-gray-400">Şirket bulunamadı</div>
            )}
          </div>
        </div>

        {/* ── RIGHT PANEL: User Table ───────────────────────────────── */}
        <div className="flex-1 bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
          {/* Right header */}
          <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-gray-900">
                {selectedOrgData ? selectedOrgData.name : 'Tüm Kullanıcılar'}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {rightUsers.length} kullanıcı ({rightActive} aktif, {rightPassive} pasif)
              </p>
            </div>
            {selectedOrgData?.subscription_status && (
              <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${planBadgeColor(selectedOrgData.subscription_status)}`}>
                {subLabel(selectedOrgData.subscription_status)}
              </span>
            )}
          </div>

          {/* Toolbar */}
          <div className="px-5 py-2.5 border-b border-gray-100 flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                placeholder="Kullanıcı ara..."
                className="h-8 pl-8 pr-3 w-40 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-400"
                data-testid="user-search-input"
              />
            </div>
            <div className="relative">
              <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                value={emailSearch}
                onChange={e => setEmailSearch(e.target.value)}
                placeholder="E-posta ara..."
                className="h-8 pl-8 pr-3 w-44 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-400"
              />
            </div>
            <FilterDropdown
              value={rightStatus}
              onChange={setRightStatus}
              options={[{ value: 'all', label: 'Tüm Durumlar', dot: '#6b7280' }, { value: 'active', label: 'Aktif', dot: '#22c55e' }, { value: 'passive', label: 'Pasif', dot: '#ef4444' }]}
            />
            <FilterDropdown value={perPage} onChange={setPerPage} options={PER_PAGE_OPTS} />
            <button onClick={fetchUsers} className="h-8 px-3 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1.5 transition-colors" data-testid="refresh-right">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Yenile
            </button>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="py-16 text-center text-sm text-gray-400">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-gray-300" />
                Yükleniyor...
              </div>
            ) : rightUsers.length === 0 ? (
              <div className="py-16 text-center">
                <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm text-gray-400">Kullanıcı bulunamadı</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80">
                    <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Kullanıcı</th>
                    <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rol</th>
                    <th className="text-center px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Şifre</th>
                    <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Durum</th>
                    <th className="text-right px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {rightUsers.map(u => {
                    const isActive = u.is_active !== false;
                    return (
                      <tr key={u.id} className="hover:bg-gray-50/60 transition-colors" data-testid={`user-row-${u.id}`}>
                        {/* Kullanıcı */}
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full ${avatarColor(u.name)} flex items-center justify-center shrink-0`}>
                              <span className="text-xs font-bold text-white">{initials(u.name || u.email || '?')}</span>
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 text-sm leading-tight">{u.name || '—'}</p>
                              <p className="text-xs text-gray-400">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        {/* Rol */}
                        <td className="px-5 py-3">
                          <span className="text-sm text-gray-600">{u.position || roleLabel(u.role)}</span>
                        </td>
                        {/* Şifre (indicator) */}
                        <td className="px-5 py-3 text-center">
                          <div className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-gray-400" title="Şifre ayarlı">
                            <Eye className="w-3.5 h-3.5" />
                          </div>
                        </td>
                        {/* Durum */}
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-md ${isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                            {isActive ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        {/* İşlemler */}
                        <td className="px-5 py-3 text-right">
                          <DropMenu>
                            <DropItem icon={User} label="Detay" onClick={() => setDetailUser(u)} />
                            {(u.role === 'organizer' || u.role === 'team_member') && (
                              <DropItem icon={LogIn} label="Kullanıcıya Geç" onClick={() => handleImpersonate(u)} />
                            )}
                            <DropItem
                              icon={isActive ? X : Check}
                              label={isActive ? 'Pasifleştir' : 'Aktifleştir'}
                              onClick={() => handleToggleStatus(u)}
                            />
                            {u.role !== 'ultra_admin' && (
                              <DropItem icon={Trash2} label="Sil" danger onClick={() => setDeleteTarget(u)} />
                            )}
                          </DropMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* ── User Detail Modal ────────────────────────────────────────── */}
      {detailUser && (
        <Dialog open={!!detailUser} onOpenChange={() => setDetailUser(null)}>
          <DialogContent className="max-w-md" data-testid="user-detail-modal">
            <DialogHeader>
              <DialogTitle>Kullanıcı Detayları</DialogTitle>
              <DialogDescription className="sr-only">Kullanıcı bilgileri</DialogDescription>
            </DialogHeader>
            <div className="space-y-5 pt-1">
              {/* Avatar + name */}
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-full ${avatarColor(detailUser.name)} flex items-center justify-center shrink-0`}>
                  <span className="text-xl font-bold text-white">{initials(detailUser.name || detailUser.email || '?')}</span>
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-base">{detailUser.name || '—'}</p>
                  <p className="text-sm text-gray-500">{detailUser.email}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold mt-1 inline-block ${detailUser.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                    {detailUser.is_active !== false ? 'Aktif' : 'Pasif'}
                  </span>
                </div>
              </div>

              {/* Details grid */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-400">Rol</p>
                  <p className="font-medium text-gray-800 mt-0.5">{roleLabel(detailUser.role)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Departman</p>
                  <p className="font-medium text-gray-800 mt-0.5">{detailUser.department || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Çalışan ID</p>
                  <p className="font-medium text-gray-800 mt-0.5 text-xs font-mono">{empId(detailUser.id)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Telefon</p>
                  <p className="font-medium text-gray-800 mt-0.5">
                    {detailUser.phone_number ? `${detailUser.phone_country_code || ''} ${detailUser.phone_number}` : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Pozisyon</p>
                  <p className="font-medium text-gray-800 mt-0.5">{detailUser.position || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Durum</p>
                  <p className="font-medium text-gray-800 mt-0.5">{detailUser.is_active !== false ? 'Aktif' : 'Pasif'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-400">Kayıt Tarihi</p>
                  <p className="font-medium text-gray-800 mt-0.5">
                    {detailUser.created_at ? new Date(detailUser.created_at).toLocaleString('tr-TR') : '—'}
                  </p>
                </div>
              </div>

              {/* Subscription info (for organizers) */}
              {(() => {
                const org = getOrgForUser(detailUser);
                if (!org?.subscription_status) return null;
                const days = daysRemaining(org.trial_ends_at);
                return (
                  <div className="border border-gray-100 rounded-xl p-4 space-y-2">
                    <p className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                      <CreditCard className="w-3.5 h-3.5 text-gray-400" /> Şirket Abonelik Bilgileri
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-gray-50 rounded-lg p-2">
                        <p className="text-[10px] text-gray-400">Abonelik Türü</p>
                        <p className="text-xs font-semibold text-teal-600 mt-0.5">{subLabel(org.subscription_status)}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2">
                        <p className="text-[10px] text-gray-400">Bitiş Tarihi</p>
                        <p className="text-xs font-semibold text-gray-700 mt-0.5">
                          {org.trial_ends_at ? new Date(org.trial_ends_at).toLocaleDateString('tr-TR') : '—'}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2">
                        <p className="text-[10px] text-gray-400">Kalan Gün</p>
                        <p className="text-xs font-semibold text-gray-700 mt-0.5">{days !== null ? `${days} gün` : '—'}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Actions */}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDetailUser(null)} className="flex-1" data-testid="detail-close-btn">
                  Kapat
                </Button>
                {(detailUser.role === 'organizer' || detailUser.role === 'team_member') && (
                  <Button
                    onClick={() => { handleImpersonate(detailUser); setDetailUser(null); }}
                    className="flex-1 bg-teal-600 hover:bg-teal-700"
                    disabled={actionLoading === detailUser.id + '_imp'}
                    data-testid={`impersonate-${detailUser.id}`}
                  >
                    <LogIn className="w-4 h-4 mr-1.5" />
                    Kullanıcıya Geç
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Delete Confirm ───────────────────────────────────────────── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setSuperPassword(''); setSuperPwdError(''); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Kullanıcıyı Sil
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email}) hesabını kalıcı olarak silmek üzeresiniz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2 space-y-1.5">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
              <Shield className="w-4 h-4 text-red-500" /> Süper Şifre
            </label>
            <div className="relative">
              <Input
                type={showSuperPwd ? 'text' : 'password'}
                placeholder="Süper şifreyi girin"
                value={superPassword}
                onChange={(e) => { setSuperPassword(e.target.value); setSuperPwdError(''); }}
                className={`pr-10 ${superPwdError ? 'border-red-400' : ''}`}
                data-testid="super-password-input"
                autoFocus
              />
              <button type="button" onClick={() => setShowSuperPwd(!showSuperPwd)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showSuperPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {superPwdError && <p className="text-xs text-red-500">{superPwdError}</p>}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setSuperPassword(''); setSuperPwdError(''); }}>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(deleteTarget); }}
              className="bg-red-600 hover:bg-red-700"
              disabled={actionLoading === deleteTarget?.id}
              data-testid="confirm-delete-btn"
            >
              {actionLoading === deleteTarget?.id ? 'Siliniyor...' : 'Sil'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
